import { FC, useRef, useState } from "react";

import {
  EventBusTopic,
  FeedbackAvoModuleName,
} from "@moodys/mdc-frontend.utils.event-bus";
import PersonIcon from "@mui/icons-material/Person";
import { LoadingButton } from "@mui/lab";
import {
  Avatar,
  Divider,
  IconButton,
  Menu,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { split } from "@moodys/federated";
import { useAccountMenuTranslation } from "components/Menu/components/AccountMenu/i18n";
import { useMenu } from "components/Menu/context";
import { useEventBus } from "hooks/useEventBus";
import {
  SPLIT_NOTIFICATION_SETTINGS,
  SPLIT_PERSON_ICON,
} from "services/split/split.constants";
import { generateHeaderAnalyticsProp } from "utils/generateAnalyticsTag";
import { getPageLanguage } from "utils/language";

import { popoutMenuWithCaretStyles } from "../../Menu.styles";

import { filterMenuModel, getUserInitials } from "./AccountMenu.helper";
import { accountMenuStyles } from "./AccountMenu.styles";
import { AccountMenuLink } from "./components/AccountMenuLink/AccountMenuLink";
import { menuModel } from "./model";
import { useEsgAuthorization } from "./useEsgAuthorization";
import { useProductAuthorizations } from "./useProductAuthorization";

export const AccountMenu: FC = () => {
  const theme = useTheme();
  const { languageNodes } = useMenu();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const { translate } = useAccountMenuTranslation(
    getPageLanguage(languageNodes)
  );
  const menuAnchor = useRef<HTMLButtonElement>(null);
  const [authenticated] = useEventBus(EventBusTopic.IsAuthenticated);
  const [, setFeedbackState] = useEventBus(EventBusTopic.Forms.FeedbackState);
  const [, setSignInModalOpen] = useEventBus(EventBusTopic.SignInModalOpen);
  const menuWithCaret = popoutMenuWithCaretStyles(theme);
  const { productAuthorizations } = useProductAuthorizations();
  const { hasEsgDocumentsContent } = useEsgAuthorization();
  const [userDetails] = useEventBus(EventBusTopic.UserDetails);

  const initials = getUserInitials(userDetails);

  const { treatments } = split.useSplitTreatments({
    names: [SPLIT_NOTIFICATION_SETTINGS, SPLIT_PERSON_ICON],
  });

  const isNotificationSettingsEnabled =
    treatments[SPLIT_NOTIFICATION_SETTINGS].treatment === "on";

  const showPersonIcon = ["on", "control"].includes(
    treatments[SPLIT_PERSON_ICON].treatment
  );

  if (!authenticated) {
    const name = translate("signInButton");
    const buttonSize = isMobile ? "small" : "large";
    return (
      <LoadingButton
        data-testid="sign-in" // Used by Dynatrace monitoring - do not remove!
        onClick={() => setSignInModalOpen(true)}
        size={buttonSize}
        variant="rounded"
        css={accountMenuStyles.loadingButton}
        loading={authenticated === undefined}
        {...generateHeaderAnalyticsProp({ name })}
      >
        {name}
      </LoadingButton>
    );
  }

  const buttonLabel = translate("openAccountMenu");

  const handleFeedbackFormClick = () => {
    setOpen(false);
    setFeedbackState({
      avoModuleName: FeedbackAvoModuleName.AccountMenu,
      modalOpen: true,
    });
  };

  const filteredMenuItems = menuModel.filter((node) =>
    filterMenuModel({
      node,
      hasEsgDocumentsContent,
      productAuthorizations,
      isNotificationSettingsEnabled,
    })
  );

  return (
    <>
      <IconButton
        data-testid="account-button" // Used by Dynatrace monitoring - do not remove!
        ref={menuAnchor}
        onClick={() => setOpen(true)}
        aria-label={buttonLabel}
        color={theme.palette.mode === "light" ? "default" : "primary"}
      >
        {showPersonIcon ? (
          <PersonIcon />
        ) : (
          <Avatar
            css={accountMenuStyles.initials(theme)}
            data-testid="account-menu-avatar"
            component="span"
          >
            {initials}
          </Avatar>
        )}
      </IconButton>
      <Menu
        container={document.getElementById("web-core-presentation")}
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={menuAnchor.current}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        css={menuWithCaret}
      >
        {filteredMenuItems.map((node, index) => {
          if (node.nodeType === "divider") {
            return <Divider key={index} />;
          }
          if (isMobile && node.hideOnMobile) {
            return null;
          }
          return (
            <AccountMenuLink
              node={node}
              key={index}
              {...(node.titleKey === "feedback" && {
                onClick: handleFeedbackFormClick,
              })}
            />
          );
        })}
      </Menu>
    </>
  );
};
